import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { getMeCached } from '../authCache';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import { fetchLibraryClaims, reviewLibraryClaim } from '../api/library';
import LibraryBookApprovalList from './components/LibraryBookApprovalList';
import LibraryApprovalEmblem from './components/LibraryApprovalEmblem';
import './LibraryPages.css';

export default function LibraryBookApprovalsPage() {
  const navigate = useNavigate();
  const classInputRef = useRef(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState('');
  const [initRetry, setInitRetry] = useState(0);
  const [listState, setListState] = useState({ classId: null, loading: false, error: '', items: [] });
  const [reload, setReload] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [message, setMessage] = useState(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let active = true;
    setInitialLoading(true);
    setInitialError('');
    (async () => {
      try {
        const me = await getMeCached();
        const id = me?.data?.admin_user?.school_id;
        if (!me?.ok || !id) throw new Error('Okul bilgisi bulunamadı.');
        const rows = await fetchClasses(id);
        if (!active) return;
        const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name, 'tr-TR'));
        setSchoolId(id);
        setClasses(sorted);
        setSelectedClass(sorted.length === 1 ? sorted[0] : null);
      } catch (err) {
        if (active) setInitialError(err.message || 'Şubeler yüklenemedi.');
      } finally {
        if (active) setInitialLoading(false);
      }
    })();
    return () => { active = false; };
  }, [initRetry]);

  useEffect(() => {
    if (!schoolId || !selectedClass) return undefined;
    let active = true;
    const classId = selectedClass.id;
    setListState({ classId, loading: true, error: '', items: [] });
    fetchLibraryClaims(schoolId, classId).then((data) => {
      if (active) setListState({ classId, loading: false, error: '', items: data.items });
    }).catch((err) => {
      if (active) setListState({ classId, loading: false, error: err.message || 'Kitap onayları yüklenemedi.', items: [] });
    });
    return () => { active = false; };
  }, [schoolId, selectedClass, reload]);

  const review = async (claimId, action, corrections) => {
    const claim = listState.items.find((item) => item.id === claimId);
    setReviewing(true);
    setActionError('');
    try {
      const result = await reviewLibraryClaim(schoolId, claimId, action, corrections);
      const alreadyCompleted = result.already_processed || (action === 'reject' && result.status === 'VERIFIED_COMPLETED');
      const text = alreadyCompleted ? 'Bu talep daha önce işlenmiş. Liste güncellendi.'
        : action === 'reject' ? 'Kitap talebi reddedildi.'
          : result.awarded_points > 0 ? `Kitap onaylandı. ${claim.student_name} +${result.awarded_points} puan kazandı.` : 'Kitap onaylandı.';
      setMessage({ text, severity: alreadyCompleted ? 'info' : 'success' });
      setListState((current) => ({ ...current, items: current.items.filter((item) => item.id !== claimId) }));
      setReload((value) => value + 1);
    } catch (err) {
      if ([403, 404, 409].includes(err.status)) {
        setActionError(err.message);
        setReload((value) => value + 1);
        return;
      }
      throw err;
    } finally {
      setReviewing(false);
    }
  };

  const currentList = selectedClass && listState.classId === selectedClass.id;
  const listLoading = selectedClass && (!currentList || listState.loading);
  const countReady = selectedClass && currentList && !listLoading && !listState.error;

  return (
    <Box className="library-page">
      <Box component="main" className="library-page__content">
        <Paper component="header" className="library-header library-approvals-header">
          <Box className="library-header__identity">
            <IconButton className="library-header__back" aria-label="Kütüphanem'e dön" onClick={() => navigate('/admin/ogrenci-kutuphanesi/ekran')}><ArrowBackIcon /></IconButton>
            <Box className="library-header__book" aria-hidden="true">📚</Box>
            <Typography component="h1">Kitap Onayları</Typography>
            <IconButton className="library-header__help" aria-label="Kitap onayları hakkında yardım" onClick={() => setHelpOpen(true)}><HelpOutlineRoundedIcon /></IconButton>
            {selectedClass && <Chip className="library-header__class" label={`📚 ${selectedClass.name}`} />}
          </Box>
          <Box className="library-header__actions">
            {classes.length > 1 && <Button className="library-class-change" variant="outlined" disabled={reviewing} onClick={() => { setSelectedClass(null); setActionError(''); classInputRef.current?.focus(); }}>Sınıf Değiştir</Button>}
          </Box>
        </Paper>
        <Paper className="library-approval-summary">
          <LibraryApprovalEmblem count={countReady ? listState.items.length : null} checked />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2">{countReady ? `${listState.items.length} kitap onay bekliyor` : 'Öğrenci kitaplarını inceleyin'}</Typography>
            <Typography className="library-approval-summary__description">Öğrencilerinizin tamamladığını bildirdiği kitapları inceleyin.</Typography>
          </Box>
        </Paper>
        {classes.length > 1 && <Paper className="library-approval-filter">
          <Typography className="library-approval-filter__label" component="span" aria-hidden="true">Şube</Typography>
            <Autocomplete options={classes} value={selectedClass} loading={initialLoading} disabled={initialLoading || reviewing || Boolean(initialError)}
              getOptionLabel={(option) => option.name || ''} isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              size="small" onChange={(_, value) => { setSelectedClass(value); setActionError(''); }}
              renderInput={(params) => <TextField {...params} inputRef={classInputRef} label="Şube" placeholder="Şube seçin" />} />
        </Paper>}
        {actionError && <Alert severity="error" onClose={() => setActionError('')}>{actionError}</Alert>}
        {initialError ? <Alert severity="error" action={<Button color="inherit" onClick={() => setInitRetry((value) => value + 1)}>Tekrar Dene</Button>}>{initialError}</Alert>
          : initialLoading || listLoading ? <Stack spacing={2} aria-label="Kitap onayları yükleniyor">{[0, 1, 2].map((index) => <Skeleton key={index} variant="rounded" height={144} sx={{ borderRadius: 4 }} />)}</Stack>
            : !selectedClass ? <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 4, textAlign: 'center' }}>
              <FactCheckOutlinedIcon sx={{ fontSize: 48, color: '#8eb3d5', mb: 1 }} />
              <Typography sx={{ fontWeight: 700 }}>{classes.length ? 'Onayları görmek için bir şube seçin.' : 'Yetkili olduğunuz şube bulunmuyor.'}</Typography>
            </Paper>
              : listState.error ? <Alert severity="error" action={<Button color="inherit" onClick={() => setReload((value) => value + 1)}>Tekrar Dene</Button>}>{listState.error}</Alert>
                : listState.items.length === 0 ? <Paper className="library-approvals-empty" sx={{ p: { xs: 3, sm: 5 }, borderRadius: 4, textAlign: 'center' }}>
                  <FactCheckOutlinedIcon sx={{ fontSize: 44, color: '#8eb3d5', mb: 1 }} aria-hidden="true" />
                  <Typography component="h2" sx={{ fontWeight: 800, fontSize: 20 }}>Tüm kitaplar incelendi! 🎉</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>Şu anda onay bekleyen kitap bulunmuyor.</Typography>
                  <Button className="library-primary" variant="contained" onClick={() => navigate('/admin/ogrenci-kutuphanesi/ekran')}>Kütüphanem'e Dön</Button>
                </Paper>
                  : <LibraryBookApprovalList key={selectedClass.id} claims={listState.items} onReview={review} />}
      </Box>
      <Snackbar open={Boolean(message)} autoHideDuration={4500} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} onClose={() => setMessage(null)}>
        <Alert className="library-result-toast" onClose={() => setMessage(null)} severity={message?.severity || 'success'}>{message?.text}</Alert>
      </Snackbar>
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth aria-labelledby="library-approval-help-title">
        <DialogTitle id="library-approval-help-title">Kitap Onayları Nasıl Çalışır?</DialogTitle>
        <DialogContent><Typography>Önce şubenizi seçin. Öğrencilerin tamamladığını bildirdiği kitapları doğrudan onaylayabilir veya bilgilerini düzenleyerek onaylayabilirsiniz. Reddedilen talepler tamamlanan kütüphaneye eklenmez.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setHelpOpen(false)}>Anladım</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
